import * as M from '@mantine/core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { handleChangeLocaleTime } from '../../../../../../../shared/utils/time';
import HoverCardText from '../../../../../../ui/hoverCardText';
import { buildSearchString } from '../../../utils/buildSearchString';

import type { IAsCsFile } from '../../../../../../../shared/apis/ai-agent/ascs/type';
import type { ManualUploadStatus } from '../../../../../../../shared/apis/ai-agent/manual/types';
import type { ApiResponse } from '../../../../../../../shared/types/response/types';

interface IAsCsFileListTableProps {
  list?: ApiResponse<IAsCsFile>;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
}

export default function AsCsFileListTable(props: IAsCsFileListTableProps) {
  const { list, page, size, onPageChange } = props;
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const navigate = useNavigate();
  const data = list?.result || [];
  const totalCount = list?.count || 0;

  const renderStatusBadge = (status: ManualUploadStatus) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <M.Badge color="green" variant="light">
            완료
          </M.Badge>
        );
      case 'IN_PROGRESS':
        return (
          <M.Badge color="blue" variant="light">
            진행중
          </M.Badge>
        );
      case 'FAILED':
        return (
          <M.Badge color="red" variant="light">
            실패
          </M.Badge>
        );
      default:
        return (
          <M.Badge color="gray" variant="light">
            대기
          </M.Badge>
        );
    }
  };
  return (
    <M.Box>
      <M.Text fw={600} mb="md">
        AS/CS 업로드 목록
      </M.Text>
      <M.Table.ScrollContainer minWidth={1000}>
        <M.Table>
          <M.Table.Thead>
            <M.Table.Tr>
              <M.Table.Th>등록 서비스</M.Table.Th>
              <M.Table.Th>파일ID</M.Table.Th>
              <M.Table.Th>파일명</M.Table.Th>
              <M.Table.Th>모델명(모델코드)</M.Table.Th>
              <M.Table.Th>적용 템플릿</M.Table.Th>
              <M.Table.Th>파일크기(KB)</M.Table.Th>
              <M.Table.Th>변환상태</M.Table.Th>
              <M.Table.Th>등록 담당자</M.Table.Th>
              <M.Table.Th>등록 일시</M.Table.Th>
            </M.Table.Tr>
          </M.Table.Thead>
          <M.Table.Tbody>
            {data.length > 0 ? (
              data.map((item) => (
                <M.Table.Tr
                  key={item.fileUploadId}
                  onClick={() =>
                    navigate(
                      `/admin/management/aiagent/company/${companyId}/ascs/file/${item.fileUploadId}/status${buildSearchString({ companyName: companyName, fileName: item.fileName })}`,
                    )
                  }
                >
                  <M.Table.Td>{item.regServiceChannel}</M.Table.Td>
                  <M.Table.Td>{item.fileUploadId}</M.Table.Td>
                  <M.Table.Td ta="left" maw={200}>
                    <HoverCardText value={item.fileName} />
                  </M.Table.Td>
                  <M.Table.Td>
                    {item.modelName}({item.modelCode})
                  </M.Table.Td>
                  <M.Table.Td ta="left" maw={200}>
                    <HoverCardText value={item.templateName} />
                  </M.Table.Td>
                  <M.Table.Td>
                    <M.NumberFormatter value={item.fileSize} thousandSeparator="," />
                  </M.Table.Td>
                  <M.Table.Td>{renderStatusBadge(item.taskStatus)}</M.Table.Td>
                  <M.Table.Td ta="left">{item.regId || '-'}</M.Table.Td>
                  <M.Table.Td>{handleChangeLocaleTime(item.regDt)}</M.Table.Td>
                </M.Table.Tr>
              ))
            ) : (
              <M.Table.Tr>
                <M.Table.Td colSpan={11} align="center">
                  데이터가 없습니다.
                </M.Table.Td>
              </M.Table.Tr>
            )}
          </M.Table.Tbody>
        </M.Table>
      </M.Table.ScrollContainer>
      {totalCount > 0 && (
        <M.Group justify="center" mt="md">
          <M.Pagination total={Math.ceil(totalCount / size)} value={page} onChange={onPageChange} />
        </M.Group>
      )}
    </M.Box>
  );
}
